import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { errorMessage } from '../admin/adminApi';
import { btnSecondary, inputClass, labelClass } from '../admin/AdminUI';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

// The Admin's part of an item's form: its readable text, images and order on the page.
function DocumentContentFields({ form, setForm, doc, api, base, notify }) {
  const [mode, setMode] = useState('html');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const canFormat = doc?.file && doc.textStatus !== 'none' && doc.textStatus !== 'not_pdf';

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadingImages(true);
    try {
      const uploaded = await Promise.all(files.map(async (file) => {
        const body = new FormData();
        body.append('image', file);
        const { data } = await api.post('/api/upload/image', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        return data;
      }));
      setForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] }));
    } catch (err) {
      alert(errorMessage(err, 'Failed to upload image. Please try again.'));
    } finally {
      setUploadingImages(false);
    }
  };

  const makeReadable = async () => {
    setFormatting(true);
    try {
      const { data } = await api.post(`${base}/${doc._id}/readable`);
      setForm((prev) => ({ ...prev, content: data.content }));
      setMode('html');
      notify('Readable version ready. Check it against the PDF, then save.');
    } catch (err) {
      alert(errorMessage(err, 'Failed to make a readable version'));
    } finally {
      setFormatting(false);
    }
  };

  const modeButton = (value, label) => (
    <button type="button" aria-pressed={mode === value} onClick={() => setMode(value)} className={`px-3 py-1 rounded border border-border text-xs ${mode === value ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground'}`}>
      {label}
    </button>
  );

  return (
    <>
      <div className="w-32">
        <label htmlFor="document-priority" className={labelClass}>Order</label>
        <input id="document-priority" type="number" min="0" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputClass} />
      </div>
      <p className="text-xs text-muted-foreground -mt-2">Lower numbers appear first in their section.</p>
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-sm font-medium text-foreground mr-auto">Readable text</span>
          {modeButton('html', 'HTML')}
          {modeButton('rich', 'Rich Text')}
          {canFormat && (
            <button type="button" onClick={makeReadable} disabled={formatting} className="text-xs px-2 py-1 rounded border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
              {formatting ? 'Formatting, this can take a few minutes...' : 'Make a readable version'}
            </button>
          )}
        </div>
        {mode === 'rich' ? (
          <div className="bg-white rounded text-black">
            <ReactQuill theme="snow" value={form.content} onChange={(content) => setForm((prev) => ({ ...prev, content }))} modules={QUILL_MODULES} />
          </div>
        ) : (
          <textarea
            aria-label="Readable text"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={14}
            placeholder="Paste or edit HTML here..."
            className="w-full px-3 py-2 bg-white border border-border rounded text-black text-sm font-mono"
          />
        )}
      </div>
      <div>
        <span className={labelClass}>Images</span>
        {form.images.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
            {form.images.map((image, index) => (
              <div key={image.key || image.url} className="relative group">
                <img src={image.url} alt={`Item ${index + 1}`} className="w-full h-24 object-cover rounded border border-border" />
                <button
                  type="button"
                  aria-label={`Remove image ${index + 1}`}
                  onClick={() => setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }))}
                  className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full px-2 opacity-0 group-hover:opacity-100 focus:opacity-100"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
        <label className="cursor-pointer inline-block">
          <input type="file" accept="image/*" multiple onChange={uploadImages} disabled={uploadingImages} className="hidden" />
          <span className={btnSecondary}>{uploadingImages ? 'Uploading...' : 'Add images'}</span>
        </label>
      </div>
    </>
  );
}

export default DocumentContentFields;
